import api from './api';
import { apiBaseUrl } from './portal';

/**
 * Raster datasets (docs/RASTER_SUPPORT.md): REST calls, the MapLibre tile URL, the
 * bearer-token transformRequest for tile fetches, and pure helpers for the RasterCard
 * and the map layer. The colour ramps mirror backend/raster.py's LUT anchors.
 */

export const COLORMAPS = ['viridis', 'turbo', 'gray', 'terrain', 'hsv'];

// Anchor colours (position 0..1, hex) — same stops as backend/raster.py _RAMPS.
export const COLORMAP_STOPS = {
  viridis: ['#440154', '#472c7a', '#3b518b', '#2c718e', '#21908d', '#27ad81', '#5cc863', '#aadc32', '#fde725']
    .map((c, i) => [i / 8, c]),
  turbo: ['#30123b', '#4662d7', '#36aaf9', '#1ae4b6', '#72fe5e', '#c8ef34', '#faba39', '#f66b19', '#ca2a04', '#7a0403']
    .map((c, i) => [i / 9, c]),
  gray: [[0, '#000000'], [1, '#ffffff']],
  terrain: [[0, '#333399'], [0.15, '#0099ff'], [0.25, '#00cc66'], [0.5, '#ffff99'], [0.75, '#805c54'], [1, '#ffffff']],
  // cyclic (DEM aspect)
  hsv: ['#ff0000', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#ff00ff', '#ff0000'].map((c, i) => [i / 6, c]),
};

export const RASTER_METHOD = 'Raster';

export const rasterApi = {
  info: (datasetId) => api.get(`/api/datasets/${datasetId}/raster/info`),
  value: (datasetId, lon, lat) => api.get(`/api/datasets/${datasetId}/raster/value`, { params: { lon, lat } }),
  // (Re)process the newest file; body { world_file?, crs? }. 202 + { job_id, job }.
  ingest: (datasetId, body = {}) => api.post(`/api/datasets/${datasetId}/raster/ingest`, body),
  depthSliceTif: (modelId, depth, property = 'resistivity') =>
    api.get(`/api/inversion/${modelId}/depth-slice.tif`, { params: { depth, property }, responseType: 'blob' }),
};

// ── Pure helpers ────────────────────────────────────────────────────────────

/** A dataset that is (or is becoming) a raster: processed metadata, a raster job, or the Raster method. */
export function isRasterDataset(ds) {
  if (!ds) return false;
  const m = ds.metadata || {};
  return !!(m.raster || m.raster_job || ds.method_type === RASTER_METHOD || ds.method_type === 'DEM');
}

/** A raster with a finished COG the tile endpoint can serve. */
export const hasProcessedRaster = (metadata) => !!metadata?.raster?.cog_path;

/** CSS linear-gradient for a colormap (legend / ramp preview). */
export function colormapGradient(name) {
  const stops = COLORMAP_STOPS[name] || COLORMAP_STOPS.viridis;
  return `linear-gradient(to right, ${stops.map(([p, c]) => `${c} ${Math.round(p * 100)}%`).join(', ')})`;
}

/** The style the map starts with: URL overrides, else metadata.raster.default_style. */
export function initialRasterStyle(raster, overrides = {}) {
  const d = raster?.default_style || {};
  const bands = d.bands || [1];
  const rgb = bands.length === 3;
  const num = (v) => (v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  return {
    bands,
    colormap: rgb ? null : (COLORMAPS.includes(overrides.colormap) ? overrides.colormap : (d.colormap || 'viridis')),
    vmin: num(overrides.vmin) ?? num(d.vmin),
    vmax: num(overrides.vmax) ?? num(d.vmax),
  };
}

/** Query string for the tile endpoint (only the parameters that are set). `style` picks a
 *  DEM product (hillshade | slope | aspect; docs/DEM_TERRAIN.md). */
export function rasterTileQuery({ bands, vmin, vmax, colormap, v, style } = {}) {
  const q = new URLSearchParams();
  if (style && style !== 'elevation') q.set('style', style);
  if (bands && bands.length) q.set('bands', bands.join(','));
  if (vmin !== null && vmin !== undefined && Number.isFinite(Number(vmin))) q.set('vmin', String(vmin));
  if (vmax !== null && vmax !== undefined && Number.isFinite(Number(vmax))) q.set('vmax', String(vmax));
  if (colormap) q.set('colormap', colormap);
  if (v) q.set('v', v);
  return q.toString();
}

/** MapLibre tile URL template ({z}/{x}/{y} placeholders kept literal). */
export function rasterTileUrl(datasetId, style = {}, base = apiBaseUrl()) {
  const qs = rasterTileQuery(style);
  return `${base}/api/datasets/${encodeURIComponent(datasetId)}/raster/tiles/{z}/{x}/{y}.png${qs ? `?${qs}` : ''}`;
}

/** [[west, south], [east, north]] from metadata.raster.bounds_wgs84, or null. */
export function rasterBounds(raster) {
  const b = raster?.bounds_wgs84;
  if (!Array.isArray(b) || b.length !== 4 || !b.every(Number.isFinite)) return null;
  return [[b[0], b[1]], [b[2], b[3]]];
}

const fmt = (v) => {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return Math.abs(n) >= 1e5 || (n !== 0 && Math.abs(n) < 1e-3) ? n.toExponential(3) : String(Number(n.toPrecision(5)));
};
export const formatRasterValue = fmt;

/** Rows for the RasterCard's info table. */
export function rasterInfoRows(r) {
  if (!r) return [];
  const mp = (r.width * r.height) / 1e6;
  return [
    ['CRS', r.crs_name ? `${r.crs} — ${r.crs_name}` : r.crs || 'Unknown'],
    ['Size', `${r.width} × ${r.height} px (${mp >= 0.1 ? mp.toFixed(1) : mp.toFixed(3)} MP)`],
    ['Bands', `${r.count} · ${(r.dtypes || []).join(', ')}`],
    ['Pixel size', Array.isArray(r.res) ? `${fmt(r.res[0])} × ${fmt(r.res[1])}` : 'Rotated / varies'],
    ['NoData', r.nodata === null || r.nodata === undefined ? 'None (mask)' : fmt(r.nodata)],
    ['Overviews', r.cog ? `${r.cog.overviews} (COG ${(r.cog.size_bytes / 1048576).toFixed(1)} MB)` : '—'],
    ['Bounds (WGS84)', Array.isArray(r.bounds_wgs84) ? r.bounds_wgs84.map((v) => Number(v).toFixed(5)).join(', ') : '—'],
  ];
}

/** Band statistics formatted for display. */
export function bandStatRows(r) {
  return (r?.bands || []).map((b) => ({
    band: b.band, label: b.description || `Band ${b.band}`,
    min: fmt(b.min), max: fmt(b.max), mean: fmt(b.mean), p2: fmt(b.p2), p98: fmt(b.p98), valid: b.valid,
  }));
}

/** "Processing" state of the dataset's raster job from metadata.raster_job. */
export function rasterJobState(metadata) {
  const j = metadata?.raster_job;
  if (!j) return { state: hasProcessedRaster(metadata) ? 'ready' : 'none' };
  if (j.status === 'queued' || j.status === 'running') return { state: 'processing', jobId: j.id };
  if (j.status === 'failed') return { state: 'failed', error: j.error || 'Raster processing failed' };
  if (j.status === 'unavailable') return { state: 'unavailable', error: j.error };
  return { state: hasProcessedRaster(metadata) ? 'ready' : 'none' };
}

const WORLD_FILE_EXT = ['pgw', 'jgw', 'tfw', 'wld', 'gfw'];
const RASTER_EXT = ['tif', 'tiff', 'png', 'jpg', 'jpeg', 'asc'];
const ext = (name) => (String(name || '').split('.').pop() || '').toLowerCase();
export const isWorldFile = (name) => WORLD_FILE_EXT.includes(ext(name));
export const isRasterFileName = (name) => RASTER_EXT.includes(ext(name));

/**
 * Upload selection -> { primary, worldFile }: a world file (.pgw/.jgw/.tfw/.wld, as
 * GeoreferencingTool exports) rides along as text with its image instead of being
 * uploaded as the dataset's file.
 */
export function splitRasterUpload(files) {
  const list = Array.from(files || []);
  const worldFile = list.find((f) => isWorldFile(f.name)) || null;
  const primary = list.find((f) => !isWorldFile(f.name)) || null;
  return { primary, worldFile: primary ? worldFile : null };
}

/** /map URL that opens the raster with a style. */
export function rasterMapUrl(datasetId, style = {}) {
  const q = new URLSearchParams({ dataset: datasetId });
  if (style.dem) q.set('dem', style.dem); // DEM product (docs/DEM_TERRAIN.md)
  if (style.colormap) q.set('colormap', style.colormap);
  if (Number.isFinite(Number(style.vmin)) && style.vmin !== null && style.vmin !== '') q.set('vmin', String(style.vmin));
  if (Number.isFinite(Number(style.vmax)) && style.vmax !== null && style.vmax !== '') q.set('vmax', String(style.vmax));
  return `/map?${q.toString()}`;
}

// ── Tile auth: MapLibre transformRequest ────────────────────────────────────
// transformRequest is synchronous, so the current access token lives in a module
// variable kept fresh from supabase.auth (startTileAuth). Only tile requests to our own
// API's raster endpoint get the header; basemaps and third-party services never do.

let tileToken = null;
export const setTileAuthToken = (token) => { tileToken = token || null; };
export const getTileAuthToken = () => tileToken;

/** True for a raster tile URL served by `base` (the API origin + path prefix). */
export function isRasterTileUrl(url, base = apiBaseUrl()) {
  if (typeof url !== 'string') return false;
  let path = url;
  if (base) {
    if (!url.startsWith(`${base}/`)) return false;
    path = url.slice(base.length);
  } else if (/^[a-z]+:\/\//i.test(url)) {
    // same-origin API: absolute URLs must be on this origin
    if (typeof window === 'undefined' || !url.startsWith(`${window.location.origin}/`)) return false;
    path = url.slice(window.location.origin.length);
  }
  return /^\/api\/datasets\/[^/]+\/raster\/tiles\/\d+\/\d+\/\d+\.png(\?|$)/.test(path);
}

/** Build a transformRequest(url, resourceType) for react-map-gl's <Map>. */
export function makeRasterTransformRequest({ base = apiBaseUrl(), getToken = getTileAuthToken } = {}) {
  return (url, resourceType) => {
    if (resourceType && resourceType !== 'Tile') return undefined;
    if (!isRasterTileUrl(url, base)) return undefined;
    const token = getToken();
    return token ? { url, headers: { Authorization: `Bearer ${token}` } } : undefined;
  };
}

export const rasterTransformRequest = makeRasterTransformRequest();

let authSub = null;
/** Keep the tile token in step with the Supabase session. Resolves once a token is known. */
export async function startTileAuth(supabase) {
  if (!authSub && supabase?.auth?.onAuthStateChange) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => setTileAuthToken(session?.access_token));
    authSub = data?.subscription || null;
  }
  try {
    const { data } = await supabase.auth.getSession();
    setTileAuthToken(data?.session?.access_token);
  } catch { /* signed out */ }
  return getTileAuthToken();
}
